import { useState } from "react";
import { apiFetch } from "./viewer";

/**
 * Give (or take back) scenes and stories on one account, by email.
 *
 * For make-goods when a run breaks, and for gifts. Nothing is sold, so no purchase is written and the sales
 * figures stay a record of real money. Looking someone up without changing anything is the zero case, which
 * is also how you check what a player actually has before promising them something.
 */

type Result = {
  account: { id: string; name: string | null; email: string; emailVerified: boolean; providers: string[]; createdAt: string };
  paid: { generations: number; games: number; gameOpen: boolean };
  used: { generations: number; games: number };
  bonus: number;
  applied: number;
};

const PROVIDER_LABELS: Record<string, string> = { credential: "email + password", google: "Google", apple: "Apple" };

export function AdminGrant({ password }: { password: string }) {
  const [email, setEmail] = useState("");
  const [units, setUnits] = useState("10");
  const [pot, setPot] = useState<"generations" | "games">("generations");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState("");

  const send = async (delta: number) => {
    setBusy(true);
    setError("");
    try {
      const res = await apiFetch("/api/admin/credit", {
        method: "POST",
        body: JSON.stringify({ password, email: email.trim(), units: delta, unit: pot }),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.error ?? `HTTP ${res.status}`);
      setResult(out);
    } catch (err) {
      setResult(null);
      setError((err as Error).message);
    }
    setBusy(false);
  };

  const amount = Math.max(0, Math.round(Number(units) || 0));
  const field = "rounded border border-white/15 bg-white/5 px-2 py-1 text-xs text-white placeholder:text-white/30";

  return (
    <div className="mb-3">
      <div className="text-white/70">Give scenes to an account</div>
      <input
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="player@example.com"
        aria-label="Account email"
        className={`mt-1 w-full ${field}`}
      />
      <div className="mt-1 flex items-center gap-1.5">
        <input
          type="number"
          min={0}
          value={units}
          onChange={e => setUnits(e.target.value)}
          aria-label="How many"
          className={`w-16 text-right ${field}`}
        />
        <select value={pot} onChange={e => setPot(e.target.value as "generations" | "games")} className={`flex-1 ${field}`}>
          <option value="generations" className="bg-black">
            scenes
          </option>
          <option value="games" className="bg-black">
            stories
          </option>
        </select>
        <button
          onClick={() => void send(amount)}
          disabled={busy || !email.trim() || !amount}
          className="rounded bg-sodium px-2 py-1 text-xs font-semibold text-black disabled:opacity-40"
          title="Add this many"
        >
          +
        </button>
        <button
          onClick={() => void send(-amount)}
          disabled={busy || !email.trim() || !amount}
          className="rounded border border-siren-red/60 px-2 py-1 text-xs text-siren-red disabled:opacity-40 hover:bg-siren-red/10"
          title="Take this many back"
        >
          −
        </button>
        <button
          onClick={() => void send(0)}
          disabled={busy || !email.trim()}
          className="rounded border border-white/15 px-2 py-1 text-[11px] text-white/60 disabled:opacity-40 hover:border-teal hover:text-teal"
          title="Look up without changing anything"
        >
          find
        </button>
      </div>

      {error && <div className="mt-1 text-xs text-siren-red">{error}</div>}

      {result && (
        <div className="mt-2 rounded border border-white/10 p-2 font-mono text-[10px] text-white/60">
          <div className="text-white">{result.account.name ?? result.account.email}</div>
          <div className="text-white/40">{result.account.email}</div>
          {/* Which sign-in methods this one account has; the same email is one account either way. */}
          <div className="mt-1 text-white/40">
            Signs in with {result.account.providers.map(p => PROVIDER_LABELS[p] ?? p).join(" + ") || "nothing yet"}
            {result.account.emailVerified ? " · email confirmed" : " · email NOT confirmed"}
          </div>
          <div className="mt-1 flex justify-between gap-2">
            <span className="text-white/40">Bought scenes</span>
            <span className="text-sodium">{result.paid.generations}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-white/40">Bought stories</span>
            <span className="text-sodium">
              {result.paid.games}
              {result.paid.gameOpen ? " (+1 in progress)" : ""}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-white/40">Used this window</span>
            <span>
              {result.used.generations} scenes · {result.used.games} stories
            </span>
          </div>
          {result.applied !== 0 && (
            <div className="mt-1 text-teal">
              {result.applied > 0 ? `Added ${result.applied}` : `Removed ${Math.abs(result.applied)}`} {pot === "games" ? "stories" : "scenes"}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
